import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Mail, ArrowRight, Loader2 } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { CONTACT, COMPANY } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = "text", value, onChange, required, textarea, placeholder }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-raritan-muted">{label}{required && " *"}</span>
    {textarea ? (
      <textarea
        data-testid={`contact-${name}`}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={5}
        placeholder={placeholder}
        className="mt-2 w-full bg-transparent border-b border-raritan-border focus:border-raritan-green outline-none py-3 font-body text-raritan-ink placeholder:text-raritan-muted/50 transition-colors resize-none"
      />
    ) : (
      <input
        data-testid={`contact-${name}`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="mt-2 w-full bg-transparent border-b border-raritan-border focus:border-raritan-green outline-none py-3 font-body text-raritan-ink placeholder:text-raritan-muted/50 transition-colors"
      />
    )}
  </label>
);

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (!payload[k]) delete payload[k]; });
      await axios.post(`${API}/contact`, payload);
      toast.success("Message sent. Our team will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", organization: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        label="Contact"
        titleLines={["Let's build", "cleaner cities", "together"]}
        intro={COMPANY.motto}
        testId="contact-hero"
      />

      <section className="px-5 md:px-10 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-14">
          {/* Info */}
          <div className="lg:col-span-4">
            <SectionLabel className="mb-8">Reach us</SectionLabel>
            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-3 mb-2 text-raritan-green"><MapPin size={18} /><span className="font-mono text-[11px] uppercase tracking-[0.15em]">Regional office</span></div>
                <p className="font-body text-raritan-ink leading-relaxed">{CONTACT.office}</p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2 text-raritan-green"><Phone size={18} /><span className="font-mono text-[11px] uppercase tracking-[0.15em]">Phone</span></div>
                {CONTACT.phones.map((p) => (
                  <a key={p} href={`tel:${p}`} className="block font-body text-raritan-ink hover:text-raritan-green transition-colors">{p}</a>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2 text-raritan-green"><Mail size={18} /><span className="font-mono text-[11px] uppercase tracking-[0.15em]">Email</span></div>
                {CONTACT.emails.map((em) => (
                  <a key={em} href={`mailto:${em}`} className="block font-body text-raritan-ink hover:text-raritan-green transition-colors break-all">{em}</a>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <Reveal>
              <form data-testid="contact-form" onSubmit={onSubmit} className="border border-raritan-border p-7 md:p-12 bg-raritan-surface">
                <h2 className="font-heading font-black uppercase tracking-tighter text-3xl md:text-4xl text-raritan-ink mb-10">Send an enquiry</h2>
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Full name" name="name" value={form.name} onChange={onChange} required placeholder="Your name" />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required placeholder="you@organization.com" />
                  <Field label="Phone" name="phone" value={form.phone} onChange={onChange} placeholder="+91" />
                  <Field label="Organization" name="organization" value={form.organization} onChange={onChange} placeholder="ULB / Institution" />
                  <div className="md:col-span-2">
                    <Field label="Subject" name="subject" value={form.subject} onChange={onChange} placeholder="How can we help?" />
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Message" name="message" value={form.message} onChange={onChange} required textarea placeholder="Tell us about your project or requirement..." />
                  </div>
                </div>
                <motion.button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  whileTap={{ scale: 0.98 }}
                  className="mt-10 inline-flex items-center gap-3 bg-raritan-green text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] hover:bg-raritan-green-hover transition-colors disabled:opacity-60 group"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
                  {loading ? "Sending..." : "Submit enquiry"}
                </motion.button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
